/**
 * Models API Schemas
 *
 * Endpoint: GET /api/models
 * Returns list of AI models from MODEL_CATALOG (seeded into DB)
 */

import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { ModelCapabilitySchema } from "../../../../models/model-capability";
import { ALL_EFFORT_LEVELS } from "../../../../models/model-catalog.config";
import { ModelControlsSchema } from "../../../../models/model-controls";
import { ThinkingDialectSchema } from "../../../../models/thinking-dialect.registry";
import { ApiSuccessSchema } from "../../../common/response.schema";

export const ModelSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(DbEnum.MODEL_PROVIDER_VALUES),
  /**
   * The kind of call the model answers — `generate` or `classify`. What a picker
   * filters on to decide where a model may be offered; never `type`, which is
   * the vendor.
   */
  capability: ModelCapabilitySchema,
  availableToAll: z.boolean(),
  organization: z.null(),

  modelName: z.string(),
  contextSize: z.number().optional(),
  streaming: z.boolean(),
  systemRole: z.enum(["system", "developer", "user"]).optional(),
  codeInterpreter: z.boolean(),
  underlyingModel: z.string().optional(),
  fastMode: z.boolean(),
  /** Thinking / reasoning dialect (absent ⇒ model does not support thinking). */
  thinkingDialect: ThinkingDialectSchema.optional(),
  maxOutputTokens: z.number().int().positive(),
  apiType: z.enum(["completions", "responses"]).optional(),
  inputTokenPrice: z.number(),
  outputTokenPrice: z.number(),
  cacheReadTokenPrice: z.number().optional(),
  cacheWriteTokenPrice: z.number().optional(),
  defaultReasoningLevel: z.enum(ALL_EFFORT_LEVELS).optional(),
  /** Pre-computed reasoning/thinking levels applicable for this model across all providers. */
  reasoningLevels: z.array(z.enum(ALL_EFFORT_LEVELS)),
  controls: ModelControlsSchema
});

const ListedModelSchema = ModelSchema.extend({
  controls: ModelControlsSchema.optional().catch(undefined)
});

export const ModelsListResponseSchema = ApiSuccessSchema(z.array(ListedModelSchema));

export type Model = z.infer<typeof ModelSchema>;
export type ModelsListResponse = z.infer<typeof ModelsListResponseSchema>;
